// State machine explícita do fluxo conversacional do GPH.
// Mantém o fluxo determinístico e impede comportamento de chat livre.

export const FLOW_STATES = Object.freeze({
  IDLE: 'idle',
  INPUT_RECEIVED: 'input_received',
  CONTEXT_CHECKING: 'context_checking',
  NEEDS_CLARIFICATION: 'needs_clarification',
  ASKING_QUESTION: 'asking_question',
  INTENT_CONFIRMED: 'intent_confirmed',
  COLLECTING_SKILL_INPUTS: 'collecting_skill_inputs',
  MOUNTING_SKILL: 'mounting_skill',
  RUNNING_SKILL: 'running_skill',
  SHOWING_RESULT: 'showing_result',
  SUGGESTING_NEXT_STEP: 'suggesting_next_step',
  COMPLETED: 'completed',
  ERROR_RECOVERY: 'error_recovery',
  PURCHASE_REQUIRED: 'purchase_required',
  SHOWING_FULL_SCREEN: 'showing_full_screen',
  UNSUPPORTED_SKILL: 'unsupported_skill',
  CONTEXTUAL_HELP: 'contextual_help',
  ADMIN_GUARD: 'admin_guard',
  ENTITLEMENT_GUARD: 'entitlement_guard',
  FREE_LIMIT_REACHED: 'free_limit_reached',
  LEGACY_ENTITLEMENT_CHECK: 'legacy_entitlement_check'
});

// Transições permitidas. Qualquer transição fora deste mapa é rejeitada
// (e registrada no console em dev) — evita estados fantasmas.
const TRANSITIONS = {
  [FLOW_STATES.IDLE]: [FLOW_STATES.INPUT_RECEIVED],
  [FLOW_STATES.INPUT_RECEIVED]: [FLOW_STATES.CONTEXT_CHECKING],
  [FLOW_STATES.CONTEXT_CHECKING]: [
    FLOW_STATES.NEEDS_CLARIFICATION,
    FLOW_STATES.INTENT_CONFIRMED,
    FLOW_STATES.UNSUPPORTED_SKILL,
    FLOW_STATES.ERROR_RECOVERY,
    FLOW_STATES.IDLE
  ],
  [FLOW_STATES.NEEDS_CLARIFICATION]: [FLOW_STATES.ASKING_QUESTION],
  [FLOW_STATES.ASKING_QUESTION]: [
    FLOW_STATES.INPUT_RECEIVED,
    FLOW_STATES.INTENT_CONFIRMED,
    FLOW_STATES.CONTEXT_CHECKING,
    FLOW_STATES.IDLE
  ],
  [FLOW_STATES.INTENT_CONFIRMED]: [
    FLOW_STATES.LEGACY_ENTITLEMENT_CHECK,
    FLOW_STATES.ENTITLEMENT_GUARD,
    FLOW_STATES.ADMIN_GUARD,
    FLOW_STATES.COLLECTING_SKILL_INPUTS,
    FLOW_STATES.MOUNTING_SKILL
  ],
  [FLOW_STATES.LEGACY_ENTITLEMENT_CHECK]: [
    FLOW_STATES.ENTITLEMENT_GUARD,
    FLOW_STATES.MOUNTING_SKILL,
    FLOW_STATES.PURCHASE_REQUIRED,
    FLOW_STATES.FREE_LIMIT_REACHED
  ],
  [FLOW_STATES.ENTITLEMENT_GUARD]: [
    FLOW_STATES.MOUNTING_SKILL,
    FLOW_STATES.PURCHASE_REQUIRED,
    FLOW_STATES.FREE_LIMIT_REACHED,
    FLOW_STATES.UNSUPPORTED_SKILL,
    FLOW_STATES.COLLECTING_SKILL_INPUTS
  ],
  [FLOW_STATES.ADMIN_GUARD]: [
    FLOW_STATES.MOUNTING_SKILL,
    FLOW_STATES.SHOWING_FULL_SCREEN,
    FLOW_STATES.SUGGESTING_NEXT_STEP,
    FLOW_STATES.IDLE
  ],
  [FLOW_STATES.COLLECTING_SKILL_INPUTS]: [
    FLOW_STATES.ASKING_QUESTION,
    FLOW_STATES.MOUNTING_SKILL,
    FLOW_STATES.IDLE
  ],
  [FLOW_STATES.MOUNTING_SKILL]: [
    FLOW_STATES.RUNNING_SKILL,
    FLOW_STATES.SHOWING_FULL_SCREEN,
    FLOW_STATES.SHOWING_RESULT,
    FLOW_STATES.ERROR_RECOVERY
  ],
  [FLOW_STATES.RUNNING_SKILL]: [
    FLOW_STATES.SHOWING_RESULT,
    FLOW_STATES.ERROR_RECOVERY,
    FLOW_STATES.SHOWING_FULL_SCREEN
  ],
  [FLOW_STATES.SHOWING_FULL_SCREEN]: [
    FLOW_STATES.SHOWING_RESULT,
    FLOW_STATES.SUGGESTING_NEXT_STEP,
    FLOW_STATES.IDLE
  ],
  [FLOW_STATES.SHOWING_RESULT]: [FLOW_STATES.SUGGESTING_NEXT_STEP, FLOW_STATES.IDLE],
  [FLOW_STATES.SUGGESTING_NEXT_STEP]: [
    FLOW_STATES.INPUT_RECEIVED,
    FLOW_STATES.COMPLETED,
    FLOW_STATES.IDLE
  ],
  [FLOW_STATES.COMPLETED]: [FLOW_STATES.IDLE, FLOW_STATES.INPUT_RECEIVED],
  [FLOW_STATES.ERROR_RECOVERY]: [FLOW_STATES.IDLE, FLOW_STATES.INPUT_RECEIVED],
  [FLOW_STATES.PURCHASE_REQUIRED]: [
    FLOW_STATES.SHOWING_FULL_SCREEN,
    FLOW_STATES.SUGGESTING_NEXT_STEP,
    FLOW_STATES.IDLE,
    FLOW_STATES.INPUT_RECEIVED
  ],
  [FLOW_STATES.UNSUPPORTED_SKILL]: [FLOW_STATES.SUGGESTING_NEXT_STEP, FLOW_STATES.IDLE, FLOW_STATES.INPUT_RECEIVED],
  [FLOW_STATES.CONTEXTUAL_HELP]: [FLOW_STATES.IDLE, FLOW_STATES.INPUT_RECEIVED],
  [FLOW_STATES.FREE_LIMIT_REACHED]: [
    FLOW_STATES.PURCHASE_REQUIRED,
    FLOW_STATES.SUGGESTING_NEXT_STEP,
    FLOW_STATES.IDLE,
    FLOW_STATES.INPUT_RECEIVED
  ]
};

export const createInitialFlowState = () => ({
  state: FLOW_STATES.IDLE,
  intent: null,
  confidence: 0,
  capabilityId: null,
  slots: {},
  missingSlots: [],
  pendingQuestion: null,
  mountedAdapter: null,
  fullScreenKey: null,
  lastError: null,
  history: [FLOW_STATES.IDLE]
});

const canTransition = (from, to) => {
  if (from === to) return true;
  const allowed = TRANSITIONS[from] || [];
  return allowed.includes(to);
};

export const flowReducer = (flow, action) => {
  switch (action.type) {
    case 'TRANSITION': {
      const { to, patch = {} } = action;
      if (!canTransition(flow.state, to)) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[GPH-FLOW] Transição inválida: ${flow.state} → ${to}`);
        }
        return flow;
      }
      return {
        ...flow,
        ...patch,
        state: to,
        history: [...flow.history.slice(-24), to]
      };
    }
    case 'SET_SLOT': {
      return {
        ...flow,
        slots: { ...flow.slots, [action.slot]: action.value },
        missingSlots: flow.missingSlots.filter((slot) => slot !== action.slot)
      };
    }
    case 'RESET':
      return createInitialFlowState();
    default:
      return flow;
  }
};

export default flowReducer;
